/*
 * Description:
 * ---------------------
 * The home's Wi-Fi channels and bands in one panel (cloud-anchor/pkg/homewifi
 * air.go, tune.go, apsettings.go). For each band: the spectrum as the home's
 * devices hear it (every network around, as wide as its channel and as tall
 * as it is loud; the home's own; the channels that need a radar wait; the
 * box's pick) over a strip of how crowded each channel is, then the access
 * point's settings to change, then each move of the band with what judging
 * it found. Band steering and whether the box moves channels by itself sit
 * at its foot. All wording lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiChannels.js
 */
import React, { useEffect, useState } from "react";
import { FiRadio, FiShuffle } from "react-icons/fi";
import { apart, useSize } from "./HomeWifiMap";
import * as W from "./homeWifiWords";

/** The access point's settings, when the box's read of them has radios with choices. */
export const apOf = (payload) => (payload?.radios?.some((r) => Array.isArray(r.widths)) ? payload : null);

const BANDS = ["2.4 GHz", "5 GHz", "6 GHz"];

export default function ChannelsPanel({ air, ap, tunes, devices, tuneMode, canWrite, busy, onRadio, onSteering, onTuneMode }) {
  const has = (list, band) => (list || []).some((r) => r.band === band);
  const bands = BANDS.filter((b) => has(air?.radios, b) || has(ap?.radios, b));
  if (!bands.length && !tuneMode && !ap?.steering) return null;
  return (
    <div className="panel hw-channels" id="hw-channels">
      <div className="panel-title">{W.CHANNELS_TITLE}</div>
      <p className="hw-quiet">{W.CHANNELS_HOW}</p>
      <div className="hw-bands">
        {bands.map((band) => {
          const ra = (air?.radios || []).find((r) => r.band === band);
          const r = (ap?.radios || []).find((x) => x.band === band);
          const moves = (tunes || []).filter((t) => t.band === band);
          const mood = ra ? W.airMood(ra.crowding) : null;
          return (
            <section key={band} className="hw-band" aria-label={band}>
              <header className="hw-band-head">
                <h4>{band}</h4>
                {mood && <span className={`hw-chip hw-chip-${mood.tone}`}>{mood.word}</span>}
                <span className="hw-band-now">{r ? W.apNow(r) : W.airNow(ra)}</span>
              </header>
              {ra && <Spectrum ra={ra} />}
              {ra && <p className="hw-band-line">{W.bandLine(ra)}</p>}
              {moves.slice(0, 3).map((t) => (
                <p key={t.id} className={`hw-move hw-move-${t.state}`} role="status">
                  <i aria-hidden="true" />
                  <span>{W.tuneSentence(t)} {W.moveVerdict(t, devices)}</span>
                </p>
              ))}
              {r && canWrite && (
                <details className="hw-band-set">
                  <summary>{W.CHANGE_BAND}</summary>
                  <RadioRow r={r} applying={moves.some((t) => t.state === "applying" || t.state === "undoing")}
                    canWrite={canWrite} busy={busy} onApply={onRadio} />
                </details>
              )}
            </section>
          );
        })}
      </div>
      {tuneMode && (
        <div className="hw-setting">
          <FiRadio aria-hidden="true" />
          <div>
            <span>{W.TUNE_SHORT}</span>
            <label className="hw-mode">
              <span>Channels</span>
              <select className="dl-input" value={tuneMode} disabled={!canWrite || busy} aria-label="When the box moves channels"
                onChange={(e) => onTuneMode(e.target.value)}>
                {W.TUNE_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}
      {ap?.steering && (
        <div className="hw-setting">
          <FiShuffle aria-hidden="true" />
          <div>
            <span>{W.STEER_SHORT}</span>
            <label className="hw-mode">
              <span>Band steering</span>
              <select className="dl-input" value={ap.steering} disabled={!canWrite || busy} aria-label="Band steering"
                onChange={(e) => onSteering(e.target.value)}>
                {W.STEER_MODES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* Each band drawn by frequency, left to right: its edges, the frequency of
   channel 0 (a channel's centre is base + 5 x its number), and the channels
   named under it. */
const SPAN = {
  "2.4 GHz": { lo: 2400, hi: 2484, base: 2407, ticks: [1, 6, 11] },
  "5 GHz": { lo: 5170, hi: 5835, base: 5000, ticks: [36, 52, 100, 116, 132, 149, 165] },
  "6 GHz": { lo: 5945, hi: 7125, base: 5950, ticks: [1, 37, 69, 101, 133, 165, 197, 229] },
};
const RADAR = [5250, 5730];
const SW = 460;
const TOP = 34;
const BASE = 146;
const PILL = 18;
const SH = BASE + PILL + 30;
const LOUD = -30;
const FAINT = -95;
const U = 11; // a label's size in the drawing

/* The band as the home's devices hear it: each network a soft wave as wide
   as its channel and as tall as it is loud where the devices are, the
   home's own a glowing wave over the whole height, the channels that need a
   radar wait tinted, the box's pick an outline, and under it how crowded
   each channel is for the home's devices, in words. Every name shows on
   hover; the two loudest are written. */
function Spectrum({ ra }) {
  // Drawn at the width it is given, a pixel a unit, so its words keep their
  // size on any screen and the band takes the room there is.
  const [ref, box] = useSize();
  const b = SPAN[ra.band];
  if (!b) return null;
  const sw = box.width || SW;
  const id = ra.band.replace(/[^0-9a-z]/gi, ""); // ids url() can name
  const x = (f) => ((Math.min(b.hi, Math.max(b.lo, f)) - b.lo) / (b.hi - b.lo)) * sw;
  const y = (dbm) => TOP + ((LOUD - Math.max(FAINT, Math.min(LOUD, dbm))) / (LOUD - FAINT)) * (BASE - TOP);
  const block = (ch, mhz) => [b.base + 5 * ch - 10, b.base + 5 * ch - 10 + (mhz || 20)];
  // A wave: flat at the channel's edges, rising smoothly to its height.
  const wave = (lo, hi, top) => {
    const [a, z] = [x(lo), x(hi)];
    const w = z - a;
    const m = (a + z) / 2;
    const f = (v) => v.toFixed(1);
    return `M${f(a)},${BASE} C${f(a + w * 0.2)},${BASE} ${f(a + w * 0.18)},${f(top)} ${f(m)},${f(top)} C${f(z - w * 0.18)},${f(top)} ${f(z - w * 0.2)},${BASE} ${f(z)},${BASE} Z`;
  };
  const around = (ra.around || []).filter((s) => s.lo_mhz && s.hi_mhz).sort((p, q) => q.loudest_dbm - p.loudest_dbm);
  const pick = W.pickOf(ra);
  const pickAt = pick ? block(pick.channel, pick.width_mhz) : null;
  const named = around.filter((s) => s.networks?.length).slice(0, 2);
  // Tags first, ours before the pick before the radar's, then the two
  // loudest neighbours' names, each kept clear of those placed before it.
  const tag = (key, cx, text, kind) => ({ key, x: cx, y: 14, text, kind, half: (text.length * 7.2 + 18) / 2 });
  const labels = apart([
    ...(ra.lo_mhz > 0 ? [tag("ours", (x(ra.lo_mhz) + x(ra.hi_mhz)) / 2, W.SPECTRUM_YOURS, "ours")] : []),
    ...(pickAt ? [tag("pick", (x(pickAt[0]) + x(pickAt[1])) / 2, W.spectrumPick(pick), "pick")] : []),
    ...(ra.band === "5 GHz" ? [tag("radar", x(RADAR[0]) + (W.SPECTRUM_RADAR.length * 7.2 + 18) / 2 + 4, W.SPECTRUM_RADAR, "radar")] : []),
    ...named.map((s, i) => ({ key: `n${i}`, x: (x(s.lo_mhz) + x(s.hi_mhz)) / 2, y: Math.max(TOP + 6, y(s.loudest_dbm) - 6), text: W.shortName(s.networks[0]) })),
  ], U, sw);
  return (
    <figure className="hw-spectrum">
      <svg ref={ref} viewBox={`0 0 ${sw} ${SH}`} role="img" aria-label={W.bandLine(ra)}>
        <defs>
          <linearGradient id={`hw-them-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#94A3B8" stopOpacity="0.42" />
            <stop offset="1" stopColor="#94A3B8" stopOpacity="0.04" />
          </linearGradient>
          <linearGradient id={`hw-ours-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#7C3AED" stopOpacity="0.55" />
            <stop offset="1" stopColor="#6366F1" stopOpacity="0.06" />
          </linearGradient>
          <filter id={`hw-glow-${id}`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#6366F1" floodOpacity="0.35" />
          </filter>
        </defs>
        {ra.band === "5 GHz" && (
          <rect x={x(RADAR[0])} y={TOP - 6} width={x(RADAR[1]) - x(RADAR[0])} height={BASE - TOP + 6} rx="12" className="hw-spec-radar" />
        )}
        {around.map((s, i) => (
          <path key={i} d={wave(s.lo_mhz, s.hi_mhz, y(s.loudest_dbm))} className={`hw-spec-wave${s.home ? " hw-spec-wave-home" : ""}`}
            fill={s.home ? `url(#hw-ours-${id})` : `url(#hw-them-${id})`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
            <title>{W.waveTitle(s)}</title>
          </path>
        ))}
        {ra.lo_mhz > 0 && (
          <path d={wave(ra.lo_mhz, ra.hi_mhz, TOP)} className="hw-spec-wave hw-spec-ours" fill={`url(#hw-ours-${id})`} filter={`url(#hw-glow-${id})`}>
            <title>{W.SPECTRUM_YOURS}</title>
          </path>
        )}
        {pickAt && <path d={wave(pickAt[0], pickAt[1], TOP + 8)} className="hw-spec-pick" />}
        <line x1="0" y1={BASE} x2={sw} y2={BASE} className="hw-spec-axis" />
        {(ra.channels || []).map((c) => {
          const [lo, hi] = block(c.channel, c.width_mhz);
          const [a, z] = [x(lo) + 2, x(hi) - 2];
          const mood = W.airMood(c.crowding);
          return (
            <g key={c.channel} className={`hw-spec-pill hw-spec-pill-${mood.tone}`}>
              <rect x={a} y={BASE + 6} width={Math.max(2, z - a)} height={PILL} rx={PILL / 2} />
              {z - a > 52 && <text x={(a + z) / 2} y={BASE + 6 + PILL / 2 + 4} textAnchor="middle">{mood.word}</text>}
            </g>
          );
        })}
        {b.ticks.map((ch) => (
          <text key={ch} x={x(b.base + 5 * ch)} y={BASE + PILL + 24} textAnchor="middle" className="hw-spec-tick">{ch}</text>
        ))}
        {labels.map((l) => (l.kind ? (
          <g key={l.key} className={`hw-spec-chip hw-spec-chip-${l.kind}`}>
            <rect x={l.x - l.half} y={l.y - 10} width={2 * l.half} height={20} rx={10} />
            <text x={l.x} y={l.y + 4} textAnchor="middle">{l.text}</text>
          </g>
        ) : (
          <text key={l.key} x={l.x} y={l.y} textAnchor="middle" className="hw-spec-label">{l.text}</text>
        )))}
      </svg>
      <figcaption className="hw-quiet">{W.spectrumKey(ra)}</figcaption>
    </figure>
  );
}

/* A band's channel, width and power to change together. A channel that
   needs a radar wait is never offered, but for the one it is on, which it
   may keep. */
function RadioRow({ r, applying, canWrite, busy, onApply }) {
  const now = { channel: r.channel, width: r.width_mhz, power: r.power_dbm };
  const [draft, setDraft] = useState(now);
  useEffect(() => setDraft({ channel: r.channel, width: r.width_mhz, power: r.power_dbm }), [r.channel, r.width_mhz, r.power_dbm]);
  const widthOf = (mhz) => r.widths.find((w) => w.mhz === mhz);
  // Its own pick is held to channels needing no radar wait, so it is
  // usable only where one is left.
  const usable = (c, mhz) => !c.radar || (c.channel !== 0 && c.channel === r.channel && mhz === r.width_mhz);
  const at = (widthOf(draft.width)?.channels || []).find((c) => c.channel === draft.channel);
  const current = (widthOf(r.width_mhz)?.channels || []).find((c) => c.channel === r.channel && c.channel !== 0);
  const max = at?.max_power_dbm || r.power_dbm;
  const changed = draft.channel !== r.channel || draft.width !== r.width_mhz || draft.power !== r.power_dbm;
  const blocked = !at ? W.apNotOffered(draft, r) : usable(at, draft.width) ? "" : W.apRadar(draft, r);
  const pickWidth = (mhz) => {
    const cs = widthOf(mhz)?.channels || [];
    // The channel it has where that width offers it, else the first there
    // that needs no radar wait.
    const c = cs.find((x) => x.channel === draft.channel && usable(x, mhz)) || cs.find((x) => usable(x, mhz));
    setDraft({ channel: c ? c.channel : draft.channel, width: mhz, power: Math.min(draft.power, c?.max_power_dbm || draft.power) });
  };
  const pickChannel = (ch) => {
    const c = (widthOf(draft.width)?.channels || []).find((x) => x.channel === ch);
    setDraft({ ...draft, channel: ch, power: Math.min(draft.power, c?.max_power_dbm || draft.power) });
  };
  return (
    <>
      {canWrite && (
        <div className="hw-ap-fields">
          <label className="hw-mode">
            <span>Channel</span>
            <select className="dl-input" value={draft.channel} disabled={busy || applying} aria-label={`${r.band} channel`}
              onChange={(e) => pickChannel(Number(e.target.value))}>
              {(widthOf(draft.width)?.channels || []).map((c) => (
                <option key={c.channel} value={c.channel} disabled={!usable(c, draft.width)}>{W.channelLabel(c)}</option>
              ))}
            </select>
          </label>
          <label className="hw-mode">
            <span>Width</span>
            <select className="dl-input" value={draft.width} disabled={busy || applying} aria-label={`${r.band} width`}
              onChange={(e) => pickWidth(Number(e.target.value))}>
              {r.widths.map((w) => (
                <option key={w.mhz} value={w.mhz} disabled={!w.channels.some((c) => usable(c, w.mhz))}>{W.widthLabel(w)}</option>
              ))}
            </select>
          </label>
          <label className="hw-mode">
            <span>Power</span>
            <select className="dl-input" value={draft.power} disabled={busy || applying} aria-label={`${r.band} power`}
              onChange={(e) => setDraft({ ...draft, power: Number(e.target.value) })}>
              {W.powerChoices(max, draft.power).map((p) => <option key={p} value={p}>{W.powerLabel(p, max)}</option>)}
            </select>
          </label>
          {changed && (
            <span className="hw-ap-go">
              <button type="button" className="dl-btn primary" disabled={!!blocked || busy || applying} onClick={() => onApply(r, draft)}>Change it</button>
              <button type="button" className="dl-btn" disabled={applying} onClick={() => setDraft(now)}>Cancel</button>
            </span>
          )}
        </div>
      )}
      {!changed && current?.radar && <p className="hw-quiet">{W.AP_ON_RADAR}</p>}
      {changed && blocked && <p className="hw-quiet" role="status">{blocked}</p>}
    </>
  );
}
